"use client"

import {
  API_ORIGIN,
  authorizedFetch,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { toast } from 'sonner'

async function readError(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}))
  return body.detail || body.error || fallback
}

export function ShareReportMenu({
  kind,
  subjectId,
}: {
  kind: 'job' | 'brief'
  subjectId: string
}) {
  const [pending, setPending] = useState(false)
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null)

  async function copy(redacted: boolean) {
    const urlPromise = authorizedFetch(`${API_ORIGIN}/api/applications/reports/links/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, subject_id: subjectId, redacted }),
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(await readError(response, 'Could not create the report link'))
      }
      const body = (await response.json()) as { url: string }
      return body.url
    })
    setPending(true)
    try {
      // Write starts in the click so the browser still treats it as the user's action.
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': urlPromise.then((url) => new Blob([url], { type: 'text/plain' })),
        }),
      ])
      setIssuedUrl(await urlPromise)
      toast.success('Link copied. It lasts seven days.')
    } catch (error) {
      try {
        setIssuedUrl(await urlPromise)
        toast.error('Could not copy the link')
      } catch (fetchError) {
        const message = fetchError instanceof Error ? fetchError.message : ''
        const fromApi = message.length > 0 && !message.startsWith('Failed to execute')
        toast.error(fromApi ? message : error instanceof Error ? error.message : 'Could not copy the link')
      }
    } finally {
      setPending(false)
    }
  }

  return (
    <span className="inline-flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={pending}>
            Share link
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={pending} onClick={() => void copy(false)}>
            Your copy
          </DropdownMenuItem>
          <DropdownMenuItem disabled={pending} onClick={() => void copy(true)}>
            Stand-in names
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {issuedUrl ? (
        <Button variant="link" size="sm" asChild>
          <a href={issuedUrl} target="_blank" rel="noopener noreferrer">
            Open report
          </a>
        </Button>
      ) : null}
    </span>
  )
}
